import MaturaCurriculumNotice from "../../components/MaturaCurriculumNotice";
import {POLYNOMIAL_GROUPING_RULE} from "../maturaCurriculumScope";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function PolynomialEquationsPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        W tym zagadnieniu rozwiązujemy równania zapisane w <strong>postaci iloczynowej</strong>,
        czyli jako mnożenie kilku czynników zakończone wynikiem <InlineMath math="0" />.
      </p>
      <details><summary style={{cursor:"pointer"}}>Dodatkowo: starsze zadania wymagające grupowania</summary>
        <MaturaCurriculumNotice rule={POLYNOMIAL_GROUPING_RULE} compact/>
        <a href="/matura/sciezka/zadania/2.2.5?archive=1">Pokaż ćwiczenia także z zadaniami archiwalnymi</a>
      </details>
      <p>Najważniejsza zasada:</p>
      <BlockMath math={String.raw`A\cdot B=0\quad\Longleftrightarrow\quad A=0\;\text{ lub }\;B=0`} />
      <p>
        Iloczyn jest równy zeru wtedy i tylko wtedy, gdy <strong>co najmniej jeden czynnik jest równy zero</strong>.
        Dlatego nie wymnażamy wszystkich nawiasów. Każdy nawias przyrównujemy osobno do zera
        i rozwiązujemy otrzymane prostsze równanie.
      </p>
      <p>Postępujemy zawsze w tej kolejności:</p>
      <ol style={{ paddingLeft: "1.4rem", marginTop: 0 }}>
        <li>sprawdzamy, czy po prawej stronie jest zero,</li>
        <li>każdy czynnik przyrównujemy do zera,</li>
        <li>rozwiązujemy powstałe równania liniowe lub kwadratowe,</li>
        <li>zbieramy wszystkie <strong>różne</strong> rozwiązania.</li>
      </ol>
      <p>
        Jeżeli ten sam wynik pojawi się z dwóch czynników, w zbiorze rozwiązań zapisujemy go tylko raz.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Dwa czynniki liniowe",
      description: "Każdy nawias przyrównujemy osobno do zera.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`(x-3)(x+5)=0`} />
          <BlockMath math={String.raw`x-3=0\quad\text{lub}\quad x+5=0`} />
          <BlockMath math={String.raw`x=3\quad\text{lub}\quad x=-5`} />
          <p><strong>Odpowiedź:</strong> <InlineMath math={String.raw`x\in\{-5,3\}`} />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Trzy czynniki liniowe",
      description: "Z trzech nawiasów mogą powstać trzy rozwiązania.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`(x+2)(x-1)(x-4)=0`} />
          <BlockMath math={String.raw`x+2=0\quad\text{lub}\quad x-1=0\quad\text{lub}\quad x-4=0`} />
          <BlockMath math={String.raw`x=-2\quad\text{lub}\quad x=1\quad\text{lub}\quad x=4`} />
          <p><strong>Odpowiedź:</strong> <InlineMath math={String.raw`x\in\{-2,1,4\}`} />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Czynnik liniowy i trójmian kwadratowy",
      description: "Jeden nawias rozwiązujemy liniowo, drugi za pomocą delty.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`(x-2)(x^2+x-6)=0`} />
          <BlockMath math={String.raw`x-2=0\quad\text{lub}\quad x^2+x-6=0`} />
          <p>Pierwszy czynnik daje <InlineMath math="x=2" />. Dla trójmianu liczymy deltę:</p>
          <BlockMath math={String.raw`\Delta=1^2-4\cdot1\cdot(-6)=25`} />
          <BlockMath math={String.raw`x_1=\frac{-1-5}{2}=-3\qquad x_2=\frac{-1+5}{2}=2`} />
          <p>
            Wynik <InlineMath math="x=2" /> pojawił się ponownie, ale liczymy go tylko raz.
          </p>
          <p><strong>Odpowiedź:</strong> <InlineMath math={String.raw`x\in\{-3,2\}`} />.</p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
